import React, { useMemo } from "react";
import { Box, FormControl, IconButton, MenuItem, Select, Slider, Tooltip, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../../context/juno/SoundContext";
import { ColumnBox } from "../../../../../../utils/styledBox";

interface DurationRange {
  min: number;
  max: number;
  step: number;
  fixedValues?: number[];
}

const DurationControl: React.FC = () => {
  const { t } = useTranslation();
  const { duration, setDuration, aiEngine } = useSound();

  // 現在選択されているエンジンのduration値を取得
  const currentDuration = duration[aiEngine];

  // モデルごとのdurationの範囲を設定
  const durationRange = useMemo<DurationRange>(() => {
    switch (aiEngine) {
      case "CASSETTEAI":
        return { min: 10, max: 180, step: 10 };
      case "DIFFRHYTHM":
        return { min: 95, max: 285, step: 190, fixedValues: [95, 285] };
      case "ELEVENLABS":
        return { min: 1, max: 22, step: 1 };
      case "MMAUDIO":
        return { min: 1, max: 30, step: 1 };
      case "ACE_STEP_AUDIO_TO_AUDIO":
      case "ACE_STEP":
      case "ACE_STEP_PROMPT_TO_AUDIO":
        return { min: 10, max: 240, step: 10 };
      default:
        return { min: 10, max: 60, step: 5 };
    }
  }, [aiEngine]);

  // DIFFRHYTHM用の特別なレンダリング（固定値のみ選択可能）
  if (aiEngine === "DIFFRHYTHM" && durationRange.fixedValues) {
    return (
      <ColumnBox>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2">
            {t("juno.sound.params.duration")}: {currentDuration}秒
          </Typography>
          <Tooltip title={t("juno.sound.params.durationHelp")} placement="top">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <FormControl fullWidth>
          <Select
            value={currentDuration}
            onChange={(e) => setDuration(Number(e.target.value))}
            displayEmpty
            aria-labelledby="duration-select"
          >
            {durationRange.fixedValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}秒
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ColumnBox>
    );
  }

  return (
    <ColumnBox>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body2">
          {t("juno.sound.params.duration")}: {currentDuration}秒
        </Typography>
        <Tooltip title={t("juno.sound.params.durationHelp")} placement="top">
          <IconButton size="small">
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Slider
        value={currentDuration}
        onChange={(_, value) => setDuration(value as number)}
        min={durationRange.min}
        max={durationRange.max}
        step={durationRange.step}
        valueLabelDisplay="auto"
        aria-labelledby="duration-slider"
        sx={{ width: "100%" }}
        marks={
          aiEngine === "CASSETTEAI" && [
            { value: 10, label: "10" },
            { value: 60, label: "60" },
            { value: 180, label: "180" },
          ]
        }
      />
    </ColumnBox>
  );
};

export default DurationControl;
